import React, { useMemo, useState } from "react";
import axios from "axios";

const panelStyle = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
};

const labelStyle = {
  display: "block",
  fontSize: 13,
  fontWeight: 700,
  color: "#334155",
  marginBottom: 6,
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid #cbd5e1",
  outline: "none",
  fontSize: 14,
};

const buttonStyle = {
  border: "none",
  borderRadius: 8,
  padding: "10px 14px",
  fontWeight: 700,
  cursor: "pointer",
};

const formatNumber = (value) => {
  const number = Number(value || 0);
  return number.toLocaleString("vi-VN");
};

const rankColorMap = {
  silver: "#64748b",
  gold: "#ca8a04",
  platinum: "#0f766e",
  diamond: "#7c3aed",
  vip: "#dc2626",
};

const MembershipResult = () => {
  const [selectedDate, setSelectedDate] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);

  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnTarget, setReturnTarget] = useState(null);
  const [rankLoading, setRankLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [returnError, setReturnError] = useState("");
  const [rankInfo, setRankInfo] = useState(null);
  const [returnAmount, setReturnAmount] = useState(0);

  const summary = useMemo(() => {
    const totalRows = data.length;
    const redRows = data.filter((item) => !item.isHaveUpdateCoin).length;
    const normalRows = totalRows - redRows;
    return { totalRows, redRows, normalRows };
  }, [data]);

  const fetchData = async () => {
    if (!selectedDate) {
      setError("Vui lòng chọn ngày");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await axios.get(
        `http://localhost:5600/booking/x/check_membership/${selectedDate}`
      );
      setData(res.data?.dataReturn || []);
      setSelectedRow(null);
      setPdfUrl("");
    } catch (err) {
      setError("Không thể tải dữ liệu");
      setData([]);
      setPdfUrl("");
    } finally {
      setLoading(false);
    }
  };

  const closeReturnModal = () => {
    setShowReturnModal(false);
    setReturnTarget(null);
    setRankInfo(null);
    setReturnAmount(0);
    setReturnError("");
    setRankLoading(false);
    setConfirmLoading(false);
  };

  const fetchUserRank = async (userId) => {
    try {
      const response = await axios.get(`http://localhost:5600/membership/user/${userId}/rank`);
      return response.data?.data || response.data;
    } catch (firstError) {
      try {
        const fallbackResponse = await axios.get(`http://localhost:5600/membership/rank/${userId}`);
        return fallbackResponse.data?.data || fallbackResponse.data;
      } catch (secondError) {
        throw secondError;
      }
    }
  };

  const openReturnModal = async (item, index) => {
    setSelectedRow(index);
    setReturnTarget(item);
    setReturnAmount(Number(item.azCoinWithReturn || 0));
    setRankInfo(null);
    setReturnError("");
    setShowReturnModal(true);
    setRankLoading(true);

    try {
      const rankData = await fetchUserRank(item.userId);
      setRankInfo(rankData);
    } catch (err) {
      setReturnError("Không thể tải rank của khách. Vẫn có thể chỉnh số coin và confirm sau khi nối đúng API.");
    } finally {
      setRankLoading(false);
    }
  };

  const handleConfirmReturnCoin = async () => {
    if (!returnTarget) return;

    setConfirmLoading(true);
    setReturnError("");

    const payload = {
      userId: returnTarget.userId,
      invoice_id: returnTarget.invoice_id,
      selectedDate,
      returnCoin: Number(returnAmount || 0),
      currentAzCoin: Number(returnTarget.azCoin || 0),
      expectedAzCoin: Number(returnTarget.newAzCoin || 0),
      currentAzPoint: Number(returnTarget.azPoint || 0),
      totalPriceUpdate: Number(returnTarget.totalPriceUpdate || 0),
      rank: rankInfo?.rank || rankInfo?.name || rankInfo?.currentRank || "",
    };

    try {
      await axios.post("http://localhost:5600/booking/x/return_membership_coin", payload);

      setData((prev) =>
        prev.map((item) =>
          item.invoice_id === returnTarget.invoice_id
            ? {
                ...item,
                isHaveUpdateCoin: true,
                azCoinWithReturn: Number(returnAmount || 0),
                newAzCoin: Number(item.azCoin || 0) + Number(returnAmount || 0),
              }
            : item
        )
      );

      closeReturnModal();
    } catch (err) {
      const serverMsg = err?.response?.data?.msg;
      setReturnError(serverMsg || "Không thể thực thi lệnh Return Coin. Kiểm tra lại endpoint backend nhé anh.");
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <div style={{ padding: 16, background: "#f8fafc", minHeight: "100vh" }}>
      <div style={{ ...panelStyle, padding: 18, marginBottom: 16 }}>
        <h2 style={{ margin: "0 0 10px", color: "#0f172a" }}>Check Membership</h2>
        <div style={{ color: "#64748b", marginBottom: 14 }}>
          Dòng đỏ là khách chưa được update coin đúng như hệ thống kỳ vọng. Có thể bấm <strong>Return Coin</strong> để xử lý tay.
        </div>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div>
            <label style={labelStyle}>Ngày kiểm tra</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              style={{ ...inputStyle, minWidth: 180 }}
            />
          </div>
          <button onClick={fetchData} style={{ ...buttonStyle, background: "#2563eb", color: "#fff" }}>
            Load data
          </button>
        </div>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 14 }}>
          <div style={{ ...panelStyle, padding: "10px 14px" }}>Tổng khách: <strong>{summary.totalRows}</strong></div>
          <div style={{ ...panelStyle, padding: "10px 14px", background: "#f0fdf4" }}>Đã OK: <strong>{summary.normalRows}</strong></div>
          <div style={{ ...panelStyle, padding: "10px 14px", background: "#fff1f2", borderColor: "#fecdd3" }}>Cần return coin: <strong>{summary.redRows}</strong></div>
        </div>
      </div>

      {loading && <p>Đang tải dữ liệu...</p>}
      {error && <p style={{ color: "red" }}>{error}</p>}

      <div style={{ display: "flex", gap: 16, width: "100%", alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ ...panelStyle, width: "calc(50% - 8px)", minWidth: 560, overflowX: "auto", padding: 12 }}>
          {data.length > 0 && (
            <table
              border="1"
              cellPadding="8"
              cellSpacing="0"
              width="100%"
              style={{ borderCollapse: "collapse", minWidth: 980 }}
            >
              <thead style={{ background: "#f3f4f6" }}>
                <tr>
                  <th>User ID</th>
                  <th>Invoice</th>
                  <th>Tổng tiền</th>
                  <th>AzCoin cũ</th>
                  <th>AzCoin +</th>
                  <th>AzCoin mới</th>
                  <th>AzPoint</th>
                  <th>Update?</th>
                  <th>PDF</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {data.map((item, index) => {
                  let rowColor = item.isHaveUpdateCoin ? "#fff" : "#ffe5e5";

                  if (selectedRow === index) {
                    rowColor = "#fff7c2";
                  }

                  return (
                    <tr
                      key={item.invoice_id || index}
                      style={{
                        backgroundColor: rowColor,
                        color: item.isHaveUpdateCoin ? "#000" : "#c00000",
                      }}
                    >
                      <td>{item.userId}</td>
                      <td>{item.invoice_id}</td>
                      <td>{formatNumber(item.totalPriceUpdate)} đ</td>
                      <td>{formatNumber(item.azCoin)}</td>
                      <td>
                        {item.isHaveUpdateCoin ? `+${formatNumber(item.azCoinWithReturn)}` : 0}
                      </td>
                      <td>{formatNumber(item.newAzCoin)}</td>
                      <td>{formatNumber(item.azPoint)}</td>
                      <td>{item.isHaveUpdateCoin ? "✔" : "❌"}</td>
                      <td>
                        {item.linkInvoice && (
                          <button
                            onClick={() => {
                              setPdfUrl(item.linkInvoice);
                              setSelectedRow(index);
                            }}
                            style={{ ...buttonStyle, background: "#e2e8f0", color: "#0f172a", padding: "8px 10px" }}
                          >
                            View PDF
                          </button>
                        )}
                      </td>
                      <td>
                        {!item.isHaveUpdateCoin && (
                          <button
                            onClick={() => openReturnModal(item, index)}
                            style={{ ...buttonStyle, background: "#dc2626", color: "#fff", padding: "8px 10px" }}
                          >
                            Return Coin
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {data.length === 0 && !loading && !error && <p>Chưa có dữ liệu</p>}
        </div>

        <div style={{ ...panelStyle, width: "calc(50% - 8px)", minWidth: 420, padding: 12 }}>
          {pdfUrl ? (
            <>
              <h3 style={{ marginTop: 0 }}>PDF Viewer</h3>
              <iframe
                src={pdfUrl}
                width="100%"
                height="600"
                style={{
                  border: "1px solid #ccc",
                  borderRadius: 4,
                  width: "100%",
                }}
                title="PDF Viewer"
              ></iframe>
            </>
          ) : (
            <p>Chọn hoá đơn để xem PDF</p>
          )}
        </div>
      </div>

      {showReturnModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: 16,
          }}
          onClick={closeReturnModal}
        >
          <div
            style={{
              width: "min(720px, 100%)",
              maxHeight: "90vh",
              overflowY: "auto",
              background: "#fff",
              borderRadius: 16,
              padding: 20,
              boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <div>
                <h3 style={{ margin: 0, color: "#0f172a" }}>Return Coin</h3>
                <div style={{ color: "#64748b", marginTop: 4 }}>Xử lý thủ công cho khách có dòng đỏ</div>
              </div>
              <button onClick={closeReturnModal} style={{ ...buttonStyle, background: "#e2e8f0", color: "#0f172a" }}>Đóng</button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 14 }}>
              <div>
                <label style={labelStyle}>User ID</label>
                <input value={returnTarget?.userId || ""} readOnly style={{ ...inputStyle, background: "#f8fafc" }} />
              </div>

              <div>
                <label style={labelStyle}>Rank</label>
                <div style={{ ...inputStyle, background: "#f8fafc", minHeight: 42, display: "flex", alignItems: "center" }}>
                  {rankLoading ? (
                    <span>Đang tải rank...</span>
                  ) : rankInfo ? (
                    <span style={{ color: rankColorMap[String(rankInfo?.rank || rankInfo?.name || "").toLowerCase()] || "#0f172a", fontWeight: 800 }}>
                      {rankInfo?.rank || rankInfo?.name || rankInfo?.currentRank || "Unknown"}
                    </span>
                  ) : (
                    <span style={{ color: "#94a3b8" }}>Chưa có dữ liệu rank</span>
                  )}
                </div>
              </div>

              <div>
                <label style={labelStyle}>Tổng tiền sẽ return</label>
                <input
                  type="number"
                  value={returnAmount}
                  onChange={(e) => setReturnAmount(e.target.value)}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Số AzCoin cũ</label>
                <input value={formatNumber(returnTarget?.azCoin)} readOnly style={{ ...inputStyle, background: "#f8fafc" }} />
              </div>

              <div>
                <label style={labelStyle}>Số AzCoin dự tính update</label>
                <input value={formatNumber(Number(returnTarget?.azCoin || 0) + Number(returnAmount || 0))} readOnly style={{ ...inputStyle, background: "#f8fafc" }} />
              </div>

              <div>
                <label style={labelStyle}>Số AzPoint đang có</label>
                <input value={formatNumber(returnTarget?.azPoint)} readOnly style={{ ...inputStyle, background: "#f8fafc" }} />
              </div>
            </div>

            {returnError && (
              <div style={{ marginTop: 16, padding: 12, borderRadius: 10, background: "#fff1f2", color: "#be123c", border: "1px solid #fecdd3" }}>
                {returnError}
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
              <button onClick={closeReturnModal} style={{ ...buttonStyle, background: "#e2e8f0", color: "#0f172a" }}>
                Cancel
              </button>
              <button
                onClick={handleConfirmReturnCoin}
                disabled={confirmLoading}
                style={{
                  ...buttonStyle,
                  background: confirmLoading ? "#94a3b8" : "#16a34a",
                  color: "#fff",
                }}
              >
                {confirmLoading ? "Đang xử lý..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembershipResult;
